import { loadStripe, StripeCardElementChangeEvent } from "@stripe/stripe-js";
import {
  WSButton,
  WSElementProps,
  WSFlexBox,
  WSForm,
  WSInput,
  WSStripeInput,
  WSStripeInputRef,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  SubscriptionPackage,
  SubscriptionPackageTier,
  SubscriptionTerm
} from "@wingspanhq/users/dist/lib/interfaces";
import * as React from "react";
import { useHistory } from "react-router-dom";
import * as Yup from "yup";
import { useUserId } from "../../../query/hooks/helpers";
import { useCreateOrUpdateSubscriptionV3 } from "../../../query/subscriptions/mutations";
import { useUserProfile } from "../../../query/users/queries";

import {
  useUpdateActivity,
  useUpdateUserProfile
} from "../../../query/users/mutations";
import { useWSStore } from "../../../store";
import {
  validatorFirstName,
  validatorLastName
} from "../../../utils/validators";
import styles from "./SubscriptionForm.module.scss";

const stripePromise = loadStripe(`${process.env.REACT_APP_STRIPE_KEY}`);

interface SubscriptionFormProps extends WSElementProps {
  packageTerm: SubscriptionTerm;
}

export const SubscriptionForm: React.FC<SubscriptionFormProps> = ({
  packageTerm
}) => {
  const history = useHistory();
  const userId = useUserId();
  const store = useWSStore();
  const qUser = useUserProfile(userId);
  const [
    createOrUpdateSubscription,
    createOrUpdateSubscriptionMeta
  ] = useCreateOrUpdateSubscriptionV3(userId);
  const [updateUser, updateUserMeta] = useUpdateUserProfile({
    throwOnError: true
  });
  const [updateActivity, updateActivityMeta] = useUpdateActivity(userId, {
    throwOnError: true
  });

  const stripeInput = React.useRef<WSStripeInputRef>(null);
  const [stripeInputState, setStripeInputState] = React.useState<
    StripeCardElementChangeEvent | undefined
  >();
  const [otherPaymentError, setOtherPaymentError] = React.useState(false);
  const [isSubmitted, setIsSubmitted] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);

  const onFormSubmit = async (data: any) => {
    setIsSubmitted(true);
    setIsLoading(true);
    setOtherPaymentError(false);

    let paymentMethodToken;

    await Promise.all([
      updateUser({
        profile: {
          firstName: data.firstName,
          lastName: data.lastName
        }
      }),
      new Promise(async (resolve, reject) => {
        if (!stripeInputState?.complete) {
          return reject("Invalid payment method!");
        }

        try {
          const result = await stripeInput?.current?.createPaymentMethod();
          if (result?.token) {
            paymentMethodToken = result?.token.id;
          }
        } catch (e) {
          console.error(e);
        }
        resolve(true);
      })
    ]);

    if (!paymentMethodToken) {
      setOtherPaymentError(true);
      setIsLoading(false);
      return;
    }

    await createOrUpdateSubscription(
      {
        paymentMethodId: paymentMethodToken,
        discountCode: data.promo || store.discountCode || "",
        package: SubscriptionPackage.Essentials,
        packageTier: SubscriptionPackageTier.Basic,
        term: packageTerm
      },
      {
        onError: () => {
          setIsLoading(false);
        }
      }
    );

    await updateActivity({
      flows: {
        onboarding: {
          version: 3,
          currentStep: 4,
          totalSteps: 4,
          complete: true
        }
      }
    });

    setIsLoading(false);
    history.push("/member/onboarding/customizing");
  };

  return (
    <WSFlexBox direction="column" p="M" className={styles.formContainer}>
      <WSForm
        onSubmit={onFormSubmit}
        defaultValues={{
          firstName: qUser.data?.profile.firstName || "",
          lastName: qUser.data?.profile.lastName || "",
          promo: ""
        }}
        validationSchema={Yup.object().shape({
          firstName: validatorFirstName.required("Required"),
          lastName: validatorLastName.required("Required")
        })}
        className={styles.form}
      >
        <WSForm.Field name="firstName" mb="S">
          <WSForm.Label>First name</WSForm.Label>
          <WSForm.Input render={WSInput} />
        </WSForm.Field>
        <WSForm.Field name="lastName" mb="S">
          <WSForm.Label>Last name</WSForm.Label>
          <WSForm.Input render={WSInput} />
        </WSForm.Field>
        {!store.discountCode && (
          <WSForm.Field name="promo" mb="S">
            <WSForm.Label>Promo code (optional)</WSForm.Label>
            <WSForm.Input render={WSInput} />
          </WSForm.Field>
        )}
        <WSForm.Field name="creditCard" mb="L">
          <WSForm.Label>Card information</WSForm.Label>
          <WSForm.Input
            render={props => (
              <WSStripeInput
                {...props}
                ref={stripeInput}
                stripeInstance={stripePromise}
                onInputChange={event => {
                  setStripeInputState(event);
                  setIsSubmitted(false);
                }}
                name="creditCard"
              />
            )}
          />
        </WSForm.Field>

        <WSButton
          fullWidth
          icon="lock"
          className={styles.tryWSBtn}
          name="tryWSFree"
          type="submit"
          loading={
            createOrUpdateSubscriptionMeta.isLoading ||
            updateUserMeta.isLoading ||
            updateActivityMeta.isLoading ||
            isLoading
          }
          mb="S"
        >
          Try Wingspan free
        </WSButton>
        {isSubmitted && !stripeInputState?.complete && (
          <WSText.LabelBold color="red400">
            Payment method is{" "}
            {stripeInputState?.empty !== false ? "required" : "invalid"}
          </WSText.LabelBold>
        )}
        {(createOrUpdateSubscriptionMeta.error || otherPaymentError) && (
          <WSText.LabelBold color="red400">
            {createOrUpdateSubscriptionMeta.error?.response?.data["error"] ||
              "Sorry! There was an error processing your payment info. Please try again."}
          </WSText.LabelBold>
        )}
        {(updateUserMeta.error || updateActivityMeta.error) && (
          <WSText.LabelBold color="red400">
            Sorry! There was an error processing your user info. Please try
            again.
          </WSText.LabelBold>
        )}
      </WSForm>
    </WSFlexBox>
  );
};
